import { debounce } from 'lodash';
import { pipe, zipObject, invert } from 'lodash/fp';
import { computed, type ComputedRef, ref, type Ref, watch } from 'vue';

import BigNumber from '@exchange/helpers/bignumber';
import { displayDefaultFailedToast } from '@exchange/libs/composables/shared/src/lib/usePromise';
import type { MarketModel } from '@exchange/libs/market/service/src';
import { MarketType } from '@exchange/libs/market/service/src/lib/market-model';
import { settingsService } from '@exchange/libs/settings/service/src';

import type { WarningModalsOrderBook } from './check-order-price';

export interface BalanceBySide {
  code: string;
  precision: number;
  available: BigNumber;
  locked: BigNumber;
  marginAvailable?: BigNumber;
}

const sliderMarks = [0, 0.25, 0.5, 0.75, 1];
const sliderPercentMarks = sliderMarks.map((m) => `${m * 100}%`);
const getNumberWithPrecision = (v, amountPrecision) => Number(v.toFixed(amountPrecision));

const getMarksObject = pipe(zipObject(sliderPercentMarks), invert);

const useOrderFormSlider = (
  market: Ref<MarketModel>,
  isMarketOrder: ComputedRef<boolean>,
  isBuyOrder: ComputedRef<boolean>,
  amountValue: Ref<string | null>,
  limitPriceValue: Ref<string | null>,
  balanceBySide: ComputedRef<BalanceBySide>,
  availableMaxAmountForCurrentSide: ComputedRef<BigNumber>,
  availableMaxAmountForPerps: ComputedRef<BigNumber>,
  isMaxAmountAvailable: ComputedRef<boolean>,
  getOrderBook: (data: { marketPrecision: number; marketId: string }) => Promise<WarningModalsOrderBook>,
) => {
  const possibleMarketOrderBuyAmount = ref(new BigNumber(0));

  const marketBuyOrder = computed(() => isMarketOrder.value && isBuyOrder.value);

  const effectiveBalance = computed(() => {
    const balance = balanceBySide.value.available;
    const isPerpMarket = market.value?.type === MarketType.PERP;

    // For perp markets, use calculated max amount (margin balance with leverage and current position taken into account)
    if (isPerpMarket) {
      return availableMaxAmountForPerps.value;
    }

    return balance;
  });

  const maxAmountForSlider = computed(() => {
    if (!market.value || !effectiveBalance.value.gt(0)) {
      return 1;
    }

    const { amountPrecision } = market.value;

    if (marketBuyOrder.value) {
      if (possibleMarketOrderBuyAmount.value.eq(0)) {
        return 1;
      }

      // For market orders, adjust the maximum amount based on slippage, this prevents insufficient funds errors when the amount * price is greater than the available balance
      const slippageMultiplier = new BigNumber(100).plus(settingsService.settings.userSettings.marketOrdersSlippage).div(100);
      const adjustedAmount = possibleMarketOrderBuyAmount.value.div(slippageMultiplier);
      return getNumberWithPrecision(adjustedAmount, amountPrecision);
    }

    if (isMaxAmountAvailable.value) {
      const amount = availableMaxAmountForCurrentSide.value;
      return getNumberWithPrecision(amount, amountPrecision);
    }

    return 10 ** -amountPrecision;
  });

  const sliderIsDisabled = computed(() => {
    if (effectiveBalance.value.lte(0)) {
      return true;
    }

    if (marketBuyOrder.value && possibleMarketOrderBuyAmount.value.eq(0)) {
      return true;
    }

    if (maxAmountForSlider.value === 0) {
      return true;
    }

    if (limitPriceValue.value !== null && Number(limitPriceValue.value) === 0) {
      return true;
    }

    return false;
  });

  const valueForSlider = computed(() => {
    if (sliderIsDisabled.value || amountValue.value === null) {
      return 0;
    }

    return Math.min(+amountValue.value, maxAmountForSlider.value);
  });

  const marksForSlider = computed(() => {
    if (maxAmountForSlider.value === 0) {
      return getMarksObject(sliderMarks);
    }

    return getMarksObject(sliderMarks.map((e) => e * maxAmountForSlider.value));
  });

  const intervalForSlider = computed(() => {
    if (!market.value || !effectiveBalance.value.gt(0)) {
      return 1;
    }

    return new BigNumber(1).shiftedBy(-market.value.amountPrecision).toNumber();
  });

  const calcPossibleMarketOrderBuyAmount = async (): Promise<BigNumber> => {
    if (!market.value || !isMarketOrder.value) {
      return new BigNumber(0);
    }

    const orderbook = await getOrderBook({
      marketPrecision: market.value.marketPrecision,
      marketId: market.value.id,
    }).catch((e) => {
      displayDefaultFailedToast();
      throw e;
    });

    const { asks } = orderbook;
    const smallestBaseValue = new BigNumber(10).pow(-market.value.amountPrecision);

    const maxBuyAmountFromFirstPrice = effectiveBalance.value.div(new BigNumber(asks[0]?.price || 1));

    let availableToSpend = effectiveBalance.value;
    let amountUserCouldBuy = new BigNumber(0);

    for (let index = 0; index < asks.length; index += 1) {
      const ask = asks[index];
      const askTotal = new BigNumber(ask?.total || 0);
      const askPrice = new BigNumber(ask?.price || 0);
      const askAmount = new BigNumber(ask?.amount || 0);

      if (availableToSpend.gte(askTotal)) {
        availableToSpend = availableToSpend.minus(askTotal);
        amountUserCouldBuy = BigNumber.sum(amountUserCouldBuy, askAmount);
      } else {
        amountUserCouldBuy = BigNumber.sum(amountUserCouldBuy, availableToSpend.div(askPrice));

        break;
      }
    }

    if (amountUserCouldBuy.lte(smallestBaseValue)) {
      return new BigNumber(0);
    }

    if (amountUserCouldBuy.gt(maxBuyAmountFromFirstPrice)) {
      return BigNumber.maximum(maxBuyAmountFromFirstPrice, 0);
    }

    return BigNumber.maximum(amountUserCouldBuy, 0);
  };

  const getPossibleMarketOrderBuyAmount = async () => {
    possibleMarketOrderBuyAmount.value = await calcPossibleMarketOrderBuyAmount();
  };

  const debouncedGetPossibleMarketOrderBuyAmount = debounce(getPossibleMarketOrderBuyAmount, 300);

  watch(amountValue, debouncedGetPossibleMarketOrderBuyAmount);
  watch(balanceBySide, getPossibleMarketOrderBuyAmount);
  watch(marketBuyOrder, getPossibleMarketOrderBuyAmount, {
    immediate: true,
  });
  watch(() => market.value?.id, getPossibleMarketOrderBuyAmount);
  watch(() => market.value?.lastPrice, getPossibleMarketOrderBuyAmount, { immediate: true });

  return {
    maxAmountForSlider,
    sliderIsDisabled,
    valueForSlider,
    marksForSlider,
    intervalForSlider,
  };
};

export default useOrderFormSlider;
